<template>
  <div v-if="hasPdfs">
    <v-menu v-if="format == 'button'">
      <template #activator="{ on, attrs}">
        <v-btn
          text
          color="black"
          v-bind="attrs"
          data-test="invoicepdfs"
          :loading="!!localeLoading"
          v-on="on"
          ><slot
            ><v-icon left>$iconReceipt</v-icon>
            {{ $t("wallet:common.downloadInvoice.button", "Download") }}</slot
          ></v-btn
        >
      </template>

      <v-list>
        <v-list-item
          v-for="locale in invoice.availableLocales"
          :key="locale"
          @click="() => getPdf(locale)"
        >
          {{
            $t(
              [
                `wallet:common.downloadInvoiceInLanguage.${locale}.label`,
                `{\{locale}\} - invoice`
              ],
              { locale: nicerLocaleLooking(locale) }
            )
          }}
        </v-list-item>
      </v-list>
    </v-menu>
    <div v-else-if="format == 'shortcuts'">
      <v-tooltip
        v-for="locale in invoice.availableLocales"
        :key="locale"
        bottom
      >
        <template #activator="{ on, attrs }">
          <v-btn
            text
            color="black"
            :loading="localeLoading == locale"
            :disabled="disableButton(locale)"
            data-test="invoicepdfShortcut"
            v-bind="attrs"
            v-on="on"
            @click="() => getPdf(locale)"
          >
            <v-icon left>$iconReceipt</v-icon>
            {{ nicerLocaleLooking(locale) }}
          </v-btn>
        </template>

        <span>
          {{
            $t(
              [
                `wallet:common.downloadInvoiceInLanguage.${locale}.tooltip`,
                "Download invoice: {\{locale}\}"
              ],
              { locale: nicerLocaleLooking(locale) }
            )
          }}
        </span>
      </v-tooltip>
    </div>
  </div>
</template>
<script>
import { defineComponent, computed, ref, getCurrentInstance } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";

export default defineComponent({
  name: "AppShowInvoicePdfs",
  props: {
    shoppingBasketId: {
      type: String,
      default: null
    },
    invoice: {
      type: Object,
      default: null
    },
    format: {
      type: String,
      default: "button",
      validate(v) {
        return ["button", "shortcuts"].include(v);
      }
    }
  },
  setup(props) {
    const instance = getCurrentInstance();
    const localeLoading = ref(null);
    const hasPdfs = computed(() => {
      return (
        instance.proxy.$allowedMutation(
          props.invoice?.allowedMutations,
          "invoiceGenerateFile"
        ) && (props.invoice?.availableLocales?.length ?? 0) > 0
      );
    });

    /**
     * Modify locale uppercase and without dash
     * @param {String} locale
     */
    function nicerLocaleLooking(locale) {
      return locale.split("-")[0].toUpperCase();
    }

    /**
     * Set other locale buttons to be disabled until the clicked locale gets response from server
     * @param {String} locale
     */
    function disableButton(locale) {
      if (!localeLoading.value) return false;
      return localeLoading.value != locale; // return true if this locale is not whats being send to server
    }

    /**
     * Generate, retrieve pdf file path and open in new tab
     * @param {String} locale
     */
    async function getPdf(locale) {
      localeLoading.value = locale;

      try {
        const { data } = await apolloClient.mutate({
          mutation: gql`
            mutation invoiceGenerateFile(
              $id: ID
              $shoppingBasketId: ID
              $locale: String
            ) {
              invoiceGenerateFile(
                input: {
                  id: $id
                  shoppingBasketId: $shoppingBasketId
                  locale: $locale
                }
              ) {
                invoice {
                  id
                  pdfFilePath(locale: $locale)
                }
              }
            }
          `,
          variables: {
            id: props.invoice.id,
            shoppingBasketId: props.shoppingBasketId,
            locale
          }
        });

        if (!data.invoiceGenerateFile.invoice.pdfFilePath) {
          localeLoading.value = null;
          return;
        }

        const btn = document.createElement("a");
        btn.href =
          process.env.VUE_APP_API_URL +
          data.invoiceGenerateFile.invoice.pdfFilePath;
        btn.target = "_blank";
        btn.click();
        btn.remove();
      } catch (error) {
        instance.proxy.$root.$snackbarServerError(error, {
          level: "error"
        });
      } finally {
        localeLoading.value = null;
      }
    }

    return {
      localeLoading,
      hasPdfs,
      nicerLocaleLooking,
      getPdf,
      disableButton
    };
  },
  fragments: {
    invoiceFields: gql`
      fragment _ on Invoice {
        id
        availableLocales
        allowedMutations
      }
    `
  }
});
</script>
